/**
 * AI Agent Chat Modal
 * Modal interface for natural language requests
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Modal, Input, Button, Spin, Alert } from 'antd';
import { SendOutlined, UndoOutlined } from '@ant-design/icons';
import { ChatMessage } from './ChatMessage';
import { EnhancedConfirmationDialog } from './EnhancedConfirmationDialog';
import { ContextSuggestions } from './ContextSuggestions';
import { useAIAgent } from '../../context/AIAgentContext';
import { debounce, requestDeduplicator, responseCache } from '../../services/ai-agent/PerformanceUtils';
import { useErrorRecovery } from '../../hooks/useErrorRecovery';
import { AIAgentErrorBoundary } from './ErrorBoundary';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputArea = styled.div`
  padding: 16px;
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: 8px;
`;

const StyledInput = styled(Input.TextArea)`
  resize: none;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Message {
  id: string;
  type: 'user' | 'agent' | 'system';
  content: string;
  timestamp: Date;
  actionPlan?: any;
  error?: boolean;
  recoveryOptions?: any[];
  requiresInput?: boolean;
  inputOptions?: any;
  conversationId?: string;
  pendingAction?: any;
}

interface ChatModalProps {
  open: boolean;
  onClose: () => void;
}

export const ChatModal: React.FC<ChatModalProps> = ({ open, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmationPlan, setConfirmationPlan] = useState<any>(null);
  const [userTrustLevel, setUserTrustLevel] = useState<'low' | 'medium' | 'high'>('low');
  const [sessionTrust, setSessionTrust] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationState, setConversationState] = useState<any>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { processRequest, executeAction, undoLastAction, currentContext } = useAIAgent();
  const { handleError, isRecovering } = useErrorRecovery();
  
  // Debounced typing indicator
  const updateTypingIndicator = useMemo(
    () => debounce((typing: boolean) => {
      setIsTyping(typing);
    }, 300),
    []
  );

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        id: '1',
        type: 'agent',
        content: 'Hi! I can help you create dashboards, navigate, or analyze your church data. What would you like to do?',
        timestamp: new Date(),
      }]);
    }
  }, [open]);

  // Optimized request processing
  const processRequestOptimized = useCallback(async (request: string) => {
    // Check cache first
    const cacheKey = `request:${request}:${currentContext?.pageType || 'unknown'}`;
    const cached = responseCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    
    // Deduplicate concurrent requests
    const result = await requestDeduplicator.deduplicate(
      cacheKey,
      () => processRequest(request)
    );
    
    // Cache successful results
    if (result.success) {
      responseCache.set(cacheKey, result);
    }
    
    return result;
  }, [processRequest, currentContext]);

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);

    try {
      // Process request with optimizations
      const requestPayload = activeConversationId
        ? { message: input.trim(), conversationId: activeConversationId, conversationState }
        : input.trim();

      const result = await processRequestOptimized(requestPayload);

      if (result.success) {
        // Check if we need to gather requirements (multi-turn conversation)
        if (result.action_type === 'gather_requirements') {
          // Start conversation flow
          setActiveConversationId(result.conversationId || Date.now().toString());
          setConversationState(result.context || {});

          // Show follow-up question
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            type: 'agent',
            content: result.message,
            timestamp: new Date(),
            requiresInput: true,
            inputOptions: result.inputOptions,
            conversationId: result.conversationId
          }]);

          // If there are specific questions to ask
          if (result.nextQuestion) {
            setTimeout(() => {
              setMessages(prev => [...prev, {
                id: Date.now().toString() + '-q',
                type: 'agent',
                content: result.nextQuestion.question,
                timestamp: new Date(),
                requiresInput: true,
                inputOptions: result.nextQuestion.options
              }]);
            }, 500);
          }
        }
        // Handle new action types that have actions array but no action_plan
        else if (result.actions && result.actions.length > 0 && !result.needs_confirmation) {
          // Check if this is a navigation action - show Proceed button instead of auto-executing
          const isNavigationAction = result.actions.some(
            (a: any) => a.type === 'navigate'
          );

          if (isNavigationAction) {
            // Store the action as pending and show Proceed button
            const actionPlan = {
              action_plan: {
                actions: result.actions,
                description: result.message,
              },
              needs_confirmation: false,
            };
            setMessages(prev => [...prev, {
              id: Date.now().toString(),
              type: 'agent',
              content: result.message,
              timestamp: new Date(),
              pendingAction: actionPlan,
            }]);
          } else {
            // Execute non-navigation actions directly
            const actionPlan = {
              action_plan: {
                actions: result.actions,
                description: result.message,
              },
              needs_confirmation: false,
            };
            await handleExecuteQuiet(actionPlan, result.message);
          }
        }
        // Check if this is a data query response (no action plan, just a message)
        else if (result.message && (!result.action_plan || (result.intent && result.intent.action_type === 'query_data'))) {
          // Display the data response directly
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            type: 'agent',
            content: result.message,
            timestamp: new Date(),
          }]);
        } else if (result.needs_confirmation) {
          // Show confirmation (convert actions array to action_plan if needed)
          if (result.actions && !result.action_plan) {
            result.action_plan = {
              actions: result.actions,
              description: result.message,
            };
          }
          setConfirmationPlan(result);
        } else if (result.action_plan) {
          // Execute immediately
          await handleExecute(result);
        } else {
          // Fallback for other success cases
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            type: 'agent',
            content: result.message || 'Request processed successfully.',
            timestamp: new Date(),
          }]);
        }
      } else {
        // Error message
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'agent',
          content: result.message || 'I had trouble understanding that request.',
          timestamp: new Date(),
          error: true,
        }]);
      }
    } catch (error) {
      console.error('Agent error:', error);
      
      // Get recovery options
      const recoveryOptions = await handleError(error as Error, {
        request: userMessage.content,
        context: currentContext,
      });
      
      if (recoveryOptions.length > 0) {
        // Show recovery options to user
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'system',
          content: 'Something went wrong. Here are your options:',
          timestamp: new Date(),
          recoveryOptions,
          error: true,
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'system',
          content: 'An error occurred. Please try again.',
          timestamp: new Date(),
          error: true,
        }]);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExecute = async (actionPlan: any) => {
    const executingMessage: Message = {
      id: Date.now().toString(),
      type: 'system',
      content: 'Executing your request...',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, executingMessage]);

    try {
      const result = await executeAction(actionPlan);

      if (result.success) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'agent',
          content: 'Done! ' + (actionPlan.action_plan?.description || 'Your request has been completed.'),
          timestamp: new Date(),
          actionPlan: actionPlan,
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'agent',
          content: `I couldn't complete that: ${result.error}`,
          timestamp: new Date(),
          error: true,
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'system',
        content: 'Failed to execute action.',
        timestamp: new Date(),
        error: true,
      }]);
    }
  };

  // Quiet execution - shows only one message for navigation actions
  const handleExecuteQuiet = async (actionPlan: any, message: string) => {
    try {
      const result = await executeAction(actionPlan);

      if (result.success) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'agent',
          content: message,
          timestamp: new Date(),
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'agent',
          content: `I couldn't complete that: ${result.error}`,
          timestamp: new Date(),
          error: true,
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'system',
        content: 'Failed to execute action.',
        timestamp: new Date(),
        error: true,
      }]);
    }
  };

  const handleUndo = async () => {
    try {
      const result = await undoLastAction();
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'agent',
        content: result.success ? 'Action undone.' : 'Unable to undo last action.',
        timestamp: new Date(),
      }]);
    } catch (error) {
      console.error('Undo error:', error);
    }
  };

  // Handle Proceed button click - execute navigation and emit event for form prefill
  const handleProceed = async (pendingAction: any) => {
    // Remove the pendingAction from the message (mark as executed)
    setMessages(prev => prev.map(msg =>
      msg.pendingAction === pendingAction
        ? { ...msg, pendingAction: undefined }
        : msg
    ));

    // Extract the navigation action and any params
    const actions = pendingAction?.action_plan?.actions || [];
    const navAction = actions.find((a: any) => a.type === 'navigate');

    if (navAction) {
      // Parse the URL to extract query params for form prefill
      const url = navAction.params?.to || navAction.params?.url || '';
      const urlParts = url.split('?');
      const basePath = urlParts[0];
      const queryString = urlParts[1] || '';

      // Parse query params
      const params: Record<string, string> = {};
      if (queryString) {
        queryString.split('&').forEach((pair: string) => {
          const [key, value] = pair.split('=');
          if (key && value) {
            params[decodeURIComponent(key)] = decodeURIComponent(value);
          }
        });
      }

      // Emit a custom event with the form data before navigation
      if (params.action === 'schedule' || params.tab === 'rooms' || Object.keys(params).length > 0) {
        // Store the prefill data in sessionStorage so the calendar page can read it
        sessionStorage.setItem('ai_prefill_data', JSON.stringify({
          action: params.action || 'schedule',
          title: params.title,
          date: params.date,
          time: params.time,
          room: params.room,
          tab: params.tab,
          timestamp: Date.now(),
        }));

        // Also emit a custom event for immediate handling
        window.dispatchEvent(new CustomEvent('ai-form-prefill', {
          detail: {
            action: params.action || 'schedule',
            title: params.title,
            date: params.date,
            time: params.time,
            room: params.room,
            tab: params.tab,
          }
        }));
      }
    }

    // Execute the navigation action
    try {
      const result = await executeAction(pendingAction);
      if (!result.success) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'agent',
          content: `Navigation failed: ${result.error}`,
          timestamp: new Date(),
          error: true,
        }]);
      } else {
        // Close the modal after successful navigation
        onClose();
      }
    } catch (error) {
      console.error('Proceed error:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'system',
        content: 'Failed to proceed with action.',
        timestamp: new Date(),
        error: true,
      }]);
    }
  };

  return (
    <>
      <Modal
        title="AI Assistant"
        open={open}
        onCancel={onClose}
        footer={null}
        width={600}
        styles={{ body: { padding: 0 } }}
      >
        <ChatContainer>
          <MessagesArea>
            {messages.length === 1 && ( // Only show on initial message
              <ContextSuggestions />
            )}
            {messages.map(message => (
              <ChatMessage key={message.id} message={message} onProceed={handleProceed} />
            ))}
            {isProcessing && (
              <div style={{ textAlign: 'center' }}>
                <Spin size="small" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>

          <InputArea>
            <StyledInput
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                updateTypingIndicator(true);
                updateTypingIndicator(false);
              }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask me anything..."
              autoSize={{ minRows: 1, maxRows: 3 }}
              disabled={isProcessing}
            />
            <ActionButtons>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                disabled={!input.trim() || isProcessing}
              />
              <Button
                icon={<UndoOutlined />}
                onClick={handleUndo}
                disabled={isProcessing || messages.length < 2}
              />
            </ActionButtons>
          </InputArea>
        </ChatContainer>
      </Modal>

      {confirmationPlan && (
        <EnhancedConfirmationDialog
          open={!!confirmationPlan}
          actionPlan={confirmationPlan}
          userTrustLevel={userTrustLevel}
          onConfirm={(trustSettings) => {
            if (trustSettings?.trustForSession) {
              setSessionTrust(true);
            }
            handleExecute(confirmationPlan);
            setConfirmationPlan(null);
          }}
          onCancel={() => {
            setConfirmationPlan(null);
            setMessages(prev => [...prev, {
              id: Date.now().toString(),
              type: 'system',
              content: 'Action cancelled.',
              timestamp: new Date(),
            }]);
          }}
        />
      )}
    </>
  );
};

// Export wrapped component with error boundary
export const ChatModalWithErrorBoundary: React.FC<ChatModalProps> = (props) => {
  return (
    <AIAgentErrorBoundary>
      <ChatModal {...props} />
    </AIAgentErrorBoundary>
  );
};